import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useLang } from "../contexts/LangContext";

const Navigation = () => {
    const { lang, setLang, t } = useLang();
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    const links = [
        { to: "/cases", label: t.nav.cases, testid: "nav-cases" },
        { to: "/privat", label: t.nav.private, testid: "nav-private" },
        { to: "/arkitekter", label: t.nav.architects, testid: "nav-architects" },
        { to: "/om-os", label: t.nav.about, testid: "nav-about" },
        { to: "/kontakt", label: t.nav.contact, testid: "nav-contact" },
    ];

    const isHome = location.pathname === "/";
    const transparent = isHome && !scrolled && !open;

    return (
        <header
            data-testid="site-navigation"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-700 ${
                transparent
                    ? "bg-transparent text-white"
                    : "bg-white/95 backdrop-blur-sm text-[#111111]"
            }`}
        >
            <div className="max-w-[1440px] mx-auto px-6 md:px-12 flex items-center justify-between h-16 md:h-20">
                <Link
                    to="/"
                    data-testid="nav-logo"
                    className="font-serif text-xl md:text-2xl tracking-tight leading-none"
                >
                    MM<span className="font-serif italic"> · </span>Byggeri
                    <span className="hidden md:inline font-serif italic text-base ml-1">
                        & Rådgivning
                    </span>
                </Link>

                <nav className="hidden md:flex items-center gap-10 text-[11px] tracking-luxury uppercase font-medium">
                    {links.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            data-testid={l.testid}
                            className={({ isActive }) =>
                                `link-underline transition-opacity ${
                                    isActive ? "opacity-100" : "opacity-70 hover:opacity-100"
                                }`
                            }
                        >
                            {l.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="flex items-center gap-4">
                    <button
                        data-testid="nav-mobile-toggle"
                        onClick={() => setOpen((v) => !v)}
                        className="md:hidden p-1 -mr-1"
                        aria-label="Menu"
                    >
                        {open ? <X size={22} /> : <Menu size={22} />}
                    </button>
                </div>
            </div>

            {/* Mobile menu */}
            <div
                className={`md:hidden bg-white text-[#111111] border-t border-[#EAE8E4] overflow-hidden transition-all duration-500 ${
                    open ? "max-h-[600px]" : "max-h-0"
                }`}
            >
                <nav className="flex flex-col px-6 py-8 gap-6 font-serif text-3xl">
                    {[{ to: "/", label: t.nav.home, testid: "mnav-home" }, ...links].map(
                        (l) => (
                            <Link
                                key={l.to}
                                to={l.to}
                                data-testid={l.testid}
                                className="block"
                            >
                                {l.label}
                            </Link>
                        ),
                    )}
                    <div className="pt-6 border-t border-[#EAE8E4]"></div>
                </nav>
            </div>
        </header>
    );
};

export default Navigation;
